export const forClients = {
  extends: 'Page',
  flow: 'y',
  background: 'surface',

  LandingNav: {},

  // ═══════════════════════════════════════════════════
  // HERO
  // ═══════════════════════════════════════════════════
  HeroSection: {
    tag: 'section',
    flow: 'y',
    align: 'center',
    gap: 'B',
    padding: 'F D E D',
    minHeight: '60vh',
    maxWidth: '1680px',
    margin: '0 auto',
    width: '100%',
    '@tabletS': { padding: 'E B D B' },
    '@mobileL': { padding: 'D A C A' },

    HeroLabel: {
      text: 'FOR CLIENTS',
      fontSize: 'Y',
      fontWeight: '600',
      color: 'green',
      textTransform: 'uppercase',
      letterSpacing: '2px'
    },

    HeroTitle: {
      tag: 'h1',
      flow: 'y',
      gap: '0',
      align: 'center',
      TitleLine1: {
        text: 'Find the right lawyer.',
        fontSize: 'C1',
        fontWeight: '300',
        color: 'text',
        lineHeight: '1.15',
        letterSpacing: '-1px',
        textAlign: 'center',
        '@mobileL': { fontSize: 'C' }
      },
      TitleLine2: {
        text: 'Book in seconds.',
        fontSize: 'C1',
        fontWeight: '300',
        color: 'text',
        lineHeight: '1.15',
        letterSpacing: '-1px',
        textAlign: 'center',
        '@mobileL': { fontSize: 'C' }
      },
      TitleLine3: {
        text: 'Stay in control.',
        fontSize: 'C1',
        fontWeight: '700',
        color: 'green',
        lineHeight: '1.15',
        letterSpacing: '-1px',
        textAlign: 'center',
        '@mobileL': { fontSize: 'C' }
      }
    },

    HeroDesc: {
      text: 'Search verified lawyers by specialty, book consultations online, track your case progress, and communicate securely — all from one place.',
      fontSize: 'A',
      color: 'textMuted',
      lineHeight: '1.6',
      maxWidth: 'J',
      textAlign: 'center'
    },

    HeroCtas: {
      flow: 'x',
      gap: 'A',
      align: 'center center',
      '@mobileL': { flow: 'y', width: '100%' },
      FindLink: {
        extends: 'Link',
        href: '/lawyers',
        text: 'Find a Lawyer',
        background: 'green',
        color: 'neutral950',
        padding: 'Z B',
        fontSize: 'Z',
        fontWeight: '600',
        borderRadius: 'B',
        textDecoration: 'none',
        transition: 'transform 200ms',
        ':hover': { transform: 'scale(0.98)' },
        '@mobileL': { width: '100%', textAlign: 'center' }
      },
      PortalLink: {
        extends: 'Link',
        href: '/client-dashboard',
        text: 'Client Portal Demo',
        background: 'transparent',
        color: 'text',
        border: '1px solid surfaceBorderStrong',
        padding: 'Z B',
        fontSize: 'Z',
        fontWeight: '500',
        borderRadius: 'B',
        textDecoration: 'none',
        transition: 'transform 200ms',
        ':hover': { transform: 'scale(0.98)' },
        '@mobileL': { width: '100%', textAlign: 'center' }
      }
    },

    HeroStats: {
      flow: 'x',
      gap: 'C',
      align: 'center center',
      padding: 'B 0 0 0',
      '@mobileL': { flow: 'y', gap: 'A' },
      Stat1: {
        flow: 'y', gap: 'Y', align: 'center',
        StatNum: { text: '5,700+', fontSize: 'C', fontWeight: '700', color: 'green', lineHeight: '1' },
        StatLabel: { text: 'Verified lawyers', fontSize: 'Y', color: 'textMuted' }
      },
      StatDiv1: { width: '1px', height: 'B', background: 'surfaceBorderStrong', '@mobileL': { display: 'none' } },
      Stat2: {
        flow: 'y', gap: 'Y', align: 'center',
        StatNum: { text: '300+', fontSize: 'C', fontWeight: '700', color: 'green', lineHeight: '1' },
        StatLabel: { text: 'Law firms', fontSize: 'Y', color: 'textMuted' }
      },
      StatDiv2: { width: '1px', height: 'B', background: 'surfaceBorderStrong', '@mobileL': { display: 'none' } },
      Stat3: {
        flow: 'y', gap: 'Y', align: 'center',
        StatNum: { text: '100%', fontSize: 'C', fontWeight: '700', color: 'green', lineHeight: '1' },
        StatLabel: { text: 'Free for clients', fontSize: 'Y', color: 'textMuted' }
      }
    }
  },

  // ═══════════════════════════════════════════════════
  // HOW IT WORKS
  // ═══════════════════════════════════════════════════
  HowSection: {
    tag: 'section',
    flow: 'y',
    gap: 'D',
    padding: 'F D',
    background: 'neutral50',
    color: 'neutral900',
    maxWidth: '1680px',
    margin: '0 auto',
    width: '100%',
    '@mobileL': { padding: 'D A' },

    HowHeader: {
      flow: 'x',
      align: 'flex-start space-between',
      '@mobileL': { flow: 'y', gap: 'A' },
      HowTitle: {
        tag: 'h2',
        text: 'How it works',
        fontSize: 'D',
        fontWeight: '700',
        color: 'neutral900',
        lineHeight: '1.1',
        '@mobileL': { fontSize: 'C' }
      },
      HowLabel: {
        text: 'SIMPLE PROCESS',
        fontSize: 'Y',
        fontWeight: '600',
        color: 'green',
        textTransform: 'uppercase',
        letterSpacing: '1px'
      }
    },

    HowSteps: {
      flow: 'x',
      gap: 'D',
      '@mobileL': { flow: 'y', gap: 'C' },

      StepsList: {
        flow: 'y',
        gap: 'B',
        flex: '1',

        Step1: {
          flow: 'x', gap: 'A', align: 'flex-start',
          cursor: 'pointer', transition: 'opacity 150ms',
          opacity: (el, s) => !s.root.fcActiveStep || s.root.fcActiveStep === 1 ? '1' : '.5',
          onClick: (e, el, s) => s.root.update({ fcActiveStep: 1 }),
          StepMarker: {
            flow: 'x', gap: 'Y', align: 'center',
            StepDot: { width: 'Z', height: 'Z', background: (el, s) => !s.root.fcActiveStep || s.root.fcActiveStep === 1 ? 'green' : 'transparent', border: (el, s) => !s.root.fcActiveStep || s.root.fcActiveStep === 1 ? 'none' : '1px solid neutral300' },
            StepNum: { text: '01', fontSize: 'Y', color: (el, s) => !s.root.fcActiveStep || s.root.fcActiveStep === 1 ? 'neutral500' : 'neutral400', fontFamily: 'monospace' }
          },
          StepContent: {
            flow: 'y', gap: 'Y',
            StepTitle: { text: 'Search & Filter', fontSize: 'A', fontWeight: '600', color: (el, s) => !s.root.fcActiveStep || s.root.fcActiveStep === 1 ? 'neutral900' : 'neutral500' },
            StepDesc: { text: 'Browse 5,700+ lawyers by specialty, city, language, and rating. Every profile is verified through the Georgian Bar Association.', fontSize: 'Z', color: 'neutral500', lineHeight: '1.6' }
          }
        },
        Step2: {
          flow: 'x', gap: 'A', align: 'flex-start',
          cursor: 'pointer', transition: 'opacity 150ms',
          opacity: (el, s) => s.root.fcActiveStep === 2 ? '1' : '.5',
          onClick: (e, el, s) => s.root.update({ fcActiveStep: 2 }),
          StepMarker: {
            flow: 'x', gap: 'Y', align: 'center',
            StepDot: { width: 'Z', height: 'Z', background: (el, s) => s.root.fcActiveStep === 2 ? 'green' : 'transparent', border: (el, s) => s.root.fcActiveStep === 2 ? 'none' : '1px solid neutral300' },
            StepNum: { text: '02', fontSize: 'Y', color: (el, s) => s.root.fcActiveStep === 2 ? 'neutral500' : 'neutral400', fontFamily: 'monospace' }
          },
          StepContent: {
            flow: 'y', gap: 'Y',
            StepTitle: { text: 'Book a Consultation', fontSize: 'A', fontWeight: '600', color: (el, s) => s.root.fcActiveStep === 2 ? 'neutral900' : 'neutral500' },
            StepDesc: { text: 'Pick a time that works for you. No phone calls needed — book online in seconds and receive instant confirmation.', fontSize: 'Z', color: 'neutral500', lineHeight: '1.6' }
          }
        },
        Step3: {
          flow: 'x', gap: 'A', align: 'flex-start',
          cursor: 'pointer', transition: 'opacity 150ms',
          opacity: (el, s) => s.root.fcActiveStep === 3 ? '1' : '.5',
          onClick: (e, el, s) => s.root.update({ fcActiveStep: 3 }),
          StepMarker: {
            flow: 'x', gap: 'Y', align: 'center',
            StepDot: { width: 'Z', height: 'Z', background: (el, s) => s.root.fcActiveStep === 3 ? 'green' : 'transparent', border: (el, s) => s.root.fcActiveStep === 3 ? 'none' : '1px solid neutral300' },
            StepNum: { text: '03', fontSize: 'Y', color: (el, s) => s.root.fcActiveStep === 3 ? 'neutral500' : 'neutral400', fontFamily: 'monospace' }
          },
          StepContent: {
            flow: 'y', gap: 'Y',
            StepTitle: { text: 'Track Your Case', fontSize: 'A', fontWeight: '600', color: (el, s) => s.root.fcActiveStep === 3 ? 'neutral900' : 'neutral500' },
            StepDesc: { text: 'Follow your case progress in real time through your personal client portal. Get updates, share documents, and message your lawyer directly.', fontSize: 'Z', color: 'neutral500', lineHeight: '1.6' }
          }
        }
      },

      StepsDemo: {
        flow: 'y',
        flex: '1',
        background: 'neutral900',
        borderRadius: 'Z',
        padding: 'B',
        gap: 'A',
        transition: 'opacity 300ms',
        '@mobileL': { display: 'none' },

        Demo1: {
          flow: 'y', gap: 'A',
          show: (el, s) => !s.root.fcActiveStep || s.root.fcActiveStep === 1,
          DemoLabel: { text: '// DIRECTORY', fontSize: 'Y', color: 'green', textTransform: 'uppercase', letterSpacing: '1px' },
          DemoMockTitle: { text: 'Corporate Law — Tbilisi', fontSize: 'A', fontWeight: '600', color: 'neutral50' },
          DemoMock1: {
            flow: 'x', align: 'center space-between', padding: 'Z', borderBottom: '1px solid neutral700',
            MockName: { text: 'Giorgi Beridze', fontSize: 'Z', color: 'neutral50' },
            MockRating: { text: '4.9 / 87 reviews', fontSize: 'Z', color: 'green', fontWeight: '600' }
          },
          DemoMock2: {
            flow: 'x', align: 'center space-between', padding: 'Z', borderBottom: '1px solid neutral700',
            MockName: { text: 'Ana Maisuradze', fontSize: 'Z', color: 'neutral50' },
            MockRating: { text: '4.9 / 102 reviews', fontSize: 'Z', color: 'neutral400' }
          },
          DemoMock3: {
            flow: 'x', align: 'center space-between', padding: 'Z',
            MockName: { text: 'Irakli Lomidze', fontSize: 'Z', color: 'neutral50' },
            MockRating: { text: '4.7 / 49 reviews', fontSize: 'Z', color: 'neutral400' }
          }
        },
        Demo2: {
          flow: 'y', gap: 'A',
          show: (el, s) => s.root.fcActiveStep === 2,
          DemoLabel: { text: '// BOOKING', fontSize: 'Y', color: 'green', textTransform: 'uppercase', letterSpacing: '1px' },
          DemoMockTitle: { text: 'Select a time slot', fontSize: 'A', fontWeight: '600', color: 'neutral50' },
          DemoMock1: {
            flow: 'x', align: 'center space-between', padding: 'Z', borderBottom: '1px solid neutral700',
            MockName: { text: 'Mon, Mar 24 — 10:00', fontSize: 'Z', color: 'neutral50' },
            MockStatus: { text: 'Available', fontSize: 'Y', color: 'green', fontWeight: '600' }
          },
          DemoMock2: {
            flow: 'x', align: 'center space-between', padding: 'Z', borderBottom: '1px solid neutral700',
            MockName: { text: 'Mon, Mar 24 — 14:00', fontSize: 'Z', color: 'neutral50' },
            MockStatus: { text: 'Available', fontSize: 'Y', color: 'green', fontWeight: '600' }
          },
          DemoMock3: {
            flow: 'x', align: 'center space-between', padding: 'Z',
            MockName: { text: 'Tue, Mar 25 — 11:00', fontSize: 'Z', color: 'neutral50' },
            MockStatus: { text: 'Available', fontSize: 'Y', color: 'green', fontWeight: '600' }
          }
        },
        Demo3: {
          flow: 'y', gap: 'A',
          show: (el, s) => s.root.fcActiveStep === 3,
          DemoLabel: { text: '// CLIENT PORTAL', fontSize: 'Y', color: 'green', textTransform: 'uppercase', letterSpacing: '1px' },
          DemoMockTitle: { text: 'Case #2024-047', fontSize: 'A', fontWeight: '600', color: 'neutral50' },
          DemoMock1: {
            flow: 'x', align: 'center space-between', padding: 'Z', borderBottom: '1px solid neutral700',
            MockName: { text: 'Status', fontSize: 'Z', color: 'neutral400' },
            MockStatus: { text: 'In Progress', fontSize: 'Y', color: 'green', fontWeight: '600' }
          },
          DemoMock2: {
            flow: 'x', align: 'center space-between', padding: 'Z', borderBottom: '1px solid neutral700',
            MockName: { text: 'Next hearing', fontSize: 'Z', color: 'neutral400' },
            MockStatus: { text: 'Apr 2, 2026', fontSize: 'Y', color: 'neutral50', fontWeight: '600' }
          },
          DemoMock3: {
            flow: 'x', align: 'center space-between', padding: 'Z',
            MockName: { text: 'Documents shared', fontSize: 'Z', color: 'neutral400' },
            MockStatus: { text: '4 files', fontSize: 'Y', color: 'neutral50' }
          }
        },

        DemoCta: {
          extends: 'Link',
          href: '/lawyers',
          text: 'BROWSE DIRECTORY',
          background: 'green',
          color: 'neutral950',
          padding: 'Z A',
          fontSize: 'Y',
          fontWeight: '600',
          borderRadius: '0',
          textDecoration: 'none',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          textAlign: 'center'
        }
      }
    }
  },

  // ═══════════════════════════════════════════════════
  // FEATURES
  // ═══════════════════════════════════════════════════
  FeaturesSection: {
    tag: 'section',
    flow: 'y',
    gap: 'D',
    padding: 'F D',
    maxWidth: '1680px',
    margin: '0 auto',
    width: '100%',
    '@mobileL': { padding: 'D A' },

    FeaturesHeader: {
      flow: 'x',
      align: 'flex-start space-between',
      '@mobileL': { flow: 'y', gap: 'A' },
      FeaturesTitle: {
        tag: 'h2',
        text: 'Everything you need\nas a client',
        fontSize: 'D',
        fontWeight: '300',
        color: 'text',
        lineHeight: '1.15',
        whiteSpace: 'pre-wrap',
        '@mobileL': { fontSize: 'C' }
      },
      FeaturesLabel: {
        text: 'FEATURES',
        fontSize: 'Y',
        fontWeight: '600',
        color: 'green',
        textTransform: 'uppercase',
        letterSpacing: '1px'
      }
    },

    FeaturesRow: {
      flow: 'x',
      gap: 'A',
      '@mobileL': { flow: 'y' },

      Feature1: {
        flow: 'y', gap: 'A', flex: '1', padding: 'B',
        background: 'surfaceCard', border: '1px solid surfaceBorderStrong',
        borderRadius: 'Z', transition: 'border-color 200ms',
        ':hover': { borderColor: 'green' },
        FeatureNum: { text: '01', fontSize: 'Y', color: 'green', fontFamily: 'monospace' },
        FeatureName: { tag: 'h3', text: 'Verified Directory', fontSize: 'B', fontWeight: '600', color: 'text' },
        FeatureDesc: { text: 'Every lawyer is cross-referenced with the Georgian Bar Association. See real ratings, specialties, languages, education, and experience.', fontSize: 'Z', color: 'textMuted', lineHeight: '1.7' }
      },
      Feature2: {
        flow: 'y', gap: 'A', flex: '1', padding: 'B',
        background: 'surfaceCard', border: '1px solid surfaceBorderStrong',
        borderRadius: 'Z', transition: 'border-color 200ms',
        ':hover': { borderColor: 'green' },
        FeatureNum: { text: '02', fontSize: 'Y', color: 'green', fontFamily: 'monospace' },
        FeatureName: { tag: 'h3', text: 'Online Booking', fontSize: 'B', fontWeight: '600', color: 'text' },
        FeatureDesc: { text: 'Book consultations directly from the lawyer\'s profile. Choose your preferred time, get instant confirmation, and receive reminders before your appointment.', fontSize: 'Z', color: 'textMuted', lineHeight: '1.7' }
      },
      Feature3: {
        flow: 'y', gap: 'A', flex: '1', padding: 'B',
        background: 'surfaceCard', border: '1px solid surfaceBorderStrong',
        borderRadius: 'Z', transition: 'border-color 200ms',
        ':hover': { borderColor: 'green' },
        FeatureNum: { text: '03', fontSize: 'Y', color: 'green', fontFamily: 'monospace' },
        FeatureName: { tag: 'h3', text: 'Client Portal', fontSize: 'B', fontWeight: '600', color: 'text' },
        FeatureDesc: { text: 'Track case progress, exchange documents securely, view invoices, and communicate with your lawyer — all from your personal dashboard.', fontSize: 'Z', color: 'textMuted', lineHeight: '1.7' }
      }
    }
  },

  // ═══════════════════════════════════════════════════
  // WHY TRUSTLEGAL
  // ═══════════════════════════════════════════════════
  WhySection: {
    tag: 'section',
    flow: 'y',
    gap: 'D',
    padding: 'F D',
    background: 'neutral50',
    color: 'neutral900',
    maxWidth: '1680px',
    margin: '0 auto',
    width: '100%',
    '@mobileL': { padding: 'D A' },

    WhyHeader: {
      flow: 'x',
      align: 'flex-start space-between',
      '@mobileL': { flow: 'y', gap: 'A' },
      WhyTitle: {
        tag: 'h2',
        text: 'Why TrustLegal?',
        fontSize: 'D',
        fontWeight: '700',
        color: 'neutral900',
        lineHeight: '1.1',
        '@mobileL': { fontSize: 'C' }
      },
      WhyLabel: {
        text: 'BENEFITS',
        fontSize: 'Y',
        fontWeight: '600',
        color: 'green',
        textTransform: 'uppercase',
        letterSpacing: '1px'
      }
    },

    WhyGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: 'B',
      '@mobileL': { gridTemplateColumns: '1fr' },

      Benefit1: {
        flow: 'y', gap: 'Z', padding: 'A',
        BenefitTitle: { text: 'No hidden costs', fontSize: 'A', fontWeight: '600', color: 'neutral900' },
        BenefitDesc: { text: 'TrustLegal is completely free for clients. You only pay the lawyer\'s consultation fee — no platform commissions.', fontSize: 'Z', color: 'neutral500', lineHeight: '1.6' }
      },
      Benefit2: {
        flow: 'y', gap: 'Z', padding: 'A',
        BenefitTitle: { text: 'Transparent reviews', fontSize: 'A', fontWeight: '600', color: 'neutral900' },
        BenefitDesc: { text: 'Read honest reviews from other clients. Ratings and feedback help you choose with confidence.', fontSize: 'Z', color: 'neutral500', lineHeight: '1.6' }
      },
      Benefit3: {
        flow: 'y', gap: 'Z', padding: 'A',
        BenefitTitle: { text: 'Secure communication', fontSize: 'A', fontWeight: '600', color: 'neutral900' },
        BenefitDesc: { text: 'All messages and documents are encrypted. Your case information stays private between you and your lawyer.', fontSize: 'Z', color: 'neutral500', lineHeight: '1.6' }
      },
      Benefit4: {
        flow: 'y', gap: 'Z', padding: 'A',
        BenefitTitle: { text: 'GBA verified', fontSize: 'A', fontWeight: '600', color: 'neutral900' },
        BenefitDesc: { text: 'Every lawyer on the platform is verified through the Georgian Bar Association database. Know your lawyer is licensed and in good standing.', fontSize: 'Z', color: 'neutral500', lineHeight: '1.6' }
      }
    }
  },

  // ═══════════════════════════════════════════════════
  // CTA
  // ═══════════════════════════════════════════════════
  CtaBanner: {
    tag: 'section',
    flow: 'y',
    align: 'center center',
    padding: 'F D',
    gap: 'B',
    textAlign: 'center',
    maxWidth: '1680px',
    margin: '0 auto',
    width: '100%',
    '@mobileL': { padding: 'D A' },

    CtaTitle: {
      tag: 'h2',
      text: 'Need a lawyer?',
      fontSize: 'D',
      fontWeight: '700',
      color: 'text',
      '@mobileL': { fontSize: 'C' }
    },
    CtaSub: {
      text: 'Search by specialty, compare profiles, and book a consultation — it takes less than a minute.',
      fontSize: 'A',
      color: 'textMuted',
      maxWidth: 'H'
    },
    CtaCtas: {
      flow: 'x',
      gap: 'A',
      align: 'center',
      FindBtn: {
        extends: 'Link',
        href: '/lawyers',
        text: 'Find a Lawyer',
        background: 'green',
        color: 'neutral950',
        padding: 'Z B',
        fontSize: 'Z',
        fontWeight: '600',
        borderRadius: 'B',
        textDecoration: 'none',
        transition: 'transform 200ms',
        ':hover': { transform: 'scale(0.98)' }
      },
      LearnBtn: {
        extends: 'Link',
        href: '/',
        text: 'Learn More',
        background: 'transparent',
        color: 'text',
        border: '1px solid surfaceBorderStrong',
        padding: 'Z B',
        fontSize: 'Z',
        fontWeight: '500',
        borderRadius: 'B',
        textDecoration: 'none',
        transition: 'transform 200ms',
        ':hover': { transform: 'scale(0.98)' }
      }
    }
  },

  // ═══════════════════════════════════════════════════
  // FOOTER
  // ═══════════════════════════════════════════════════
  FooterSection: {
    tag: 'footer',
    flow: 'y',
    gap: 'C',
    padding: 'D 0',
    borderTop: '1px solid surfaceBorderStrong',
    maxWidth: '1680px',
    margin: '0 auto',
    width: '100%',
    '@mobileL': { padding: 'C A' },

    FooterTop: {
      flow: 'x', gap: 'C', '@mobileL': { flow: 'y', gap: 'B' },
      FooterBrand: {
        flow: 'y', gap: 'A', flex: '1.5', minWidth: 'F',
        FooterLogo: { extends: 'Link', href: '/', text: 'TrustLegal', fontSize: 'B', fontWeight: '800', color: 'green', textDecoration: 'none' },
        FooterTagline: { text: '{{ footerTagline | polyglot }}', fontSize: 'Z', color: 'textMuted', lineHeight: '1.6' }
      },
      FooterLinks: {
        flow: 'x', gap: 'C', flex: '2', '@mobileL': { flow: 'y', gap: 'B' },
        FooterCol1: {
          flow: 'y', gap: 'Z', flex: '1',
          ColTitle: { text: '{{ productLabel | polyglot }}', fontSize: 'Y', fontWeight: '600', color: 'textMuted', textTransform: 'uppercase', letterSpacing: '1px', padding: '0 0 Z 0' },
          ColLink1: { extends: 'Link', href: '/lawyers', text: 'Find a Lawyer', fontSize: 'Z', color: 'green', textDecoration: 'none', fontWeight: '500' },
          ColLink2: { extends: 'Link', href: '/for-lawyers', text: 'For Lawyers', fontSize: 'Z', color: 'textMuted', textDecoration: 'none', ':hover': { color: 'text' } }
        },
        FooterCol2: {
          flow: 'y', gap: 'Z', flex: '1',
          ColTitle: { text: '{{ companyLabel | polyglot }}', fontSize: 'Y', fontWeight: '600', color: 'textMuted', textTransform: 'uppercase', letterSpacing: '1px', padding: '0 0 Z 0' },
          ColLink1: { extends: 'Link', href: '/', text: '{{ aboutUs | polyglot }}', fontSize: 'Z', color: 'textMuted', textDecoration: 'none', ':hover': { color: 'text' } },
          ColLink2: { extends: 'Link', href: '/', text: '{{ careers | polyglot }}', fontSize: 'Z', color: 'textMuted', textDecoration: 'none', ':hover': { color: 'text' } }
        }
      }
    },
    FooterBottom: {
      flow: 'x', align: 'center space-between', padding: 'A 0 0 0', borderTop: '1px solid surfaceBorderStrong',
      '@mobileL': { flow: 'y', gap: 'Z', align: 'center center' },
      CopyText: { text: '{{ copyrightText | polyglot }}', fontSize: 'Z', color: 'textMuted' },
      BuiltWith: { text: '{{ builtWith | polyglot }}', fontSize: 'Z', color: 'textMuted' }
    }
  }
}
